import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { theme } from 'antd'
import { DownOutlined, PlusOutlined } from '@ant-design/icons'
import { fontSize, fontWeight, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { countryName } from '@vacationtracker/shared/data/countries'
import { groupByCountry } from '@vacationtracker/shared/functions/location-country-groups'

import { Button } from '../../../../design-system/Button'
import { Text } from '../../../../design-system/Text'
import { FrontendUrls } from '../../../../types/urls'
import { IGetLocations } from '../../../../types/locations'

const ADD_CLASS = 'vt-locations-add-in-country'
// A touch screen has no hover to reveal the add button, so it always shows there.
const addButtonStyles = `@media (hover: none) { .${ADD_CLASS} { opacity: 1 !important; pointer-events: auto !important; } }`

interface ICountryGroups {
  locations: IGetLocations[]
  /** The page's own Create button is enabled. */
  canCreate: boolean
  renderCards: (locations: IGetLocations[], firstGroup: boolean) => React.ReactNode
}

/** Opens Create location with this country already chosen. Nothing for a code the country list does not know. */
const AddInCountryButton = ({ countryCode }: { countryCode: string }): React.ReactElement | null => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const country = countryName(countryCode)
  if (!country) return null
  return (
    <Button variant="text" size="small" icon={<PlusOutlined />} onClick={() => history.push(`${FrontendUrls.locations}/create?country=${countryCode}`)}>
      {formatMessage({ id: 'locations.addInCountry' }, { country })}
    </Button>
  )
}

/** The locations under a heading per country. Every country starts open; the whole heading row opens and closes it. */
export const CountryGroups = ({ locations, canCreate, renderCards }: ICountryGroups): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const [collapsed, setCollapsed] = useState<string[]>([])
  const [activeHeading, setActiveHeading] = useState<string | null>(null)

  const toggle = (key: string) => setCollapsed((current) => (current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]))

  return (
    <>
      <style>{addButtonStyles}</style>
      {groupByCountry(locations, formatMessage({ id: 'locationPlace.card.noCountrySet' })).map((group, groupIndex, groups) => {
        const open = !collapsed.includes(group.key)
        const afterClosed = groupIndex > 0 && collapsed.includes(groups[groupIndex - 1].key)
        const panelId = `locations-country-${group.key}`
        const active = activeHeading === group.key
        return (
          // A rule only under a closed country: open ones are already set apart by their cards.
          <section key={group.key} style={{ padding: `${spacing.sm}px 0`, borderTop: afterClosed ? `1px solid ${token.colorSplit}` : undefined }}>
            {/* The whole row toggles and tints on hover; the add button keeps its own click. */}
            <div
              onClick={(event) => {
                if (!(event.target as Element).closest(`.${ADD_CLASS}`)) toggle(group.key)
              }}
              onMouseEnter={() => setActiveHeading(group.key)}
              onMouseLeave={() => setActiveHeading((current) => (current === group.key ? null : current))}
              onFocus={() => setActiveHeading(group.key)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setActiveHeading(null)
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: spacing.sm,
                margin: `0 -${spacing.xs}px`,
                padding: `0 ${spacing.xs}px`,
                minHeight: 40,
                borderRadius: token.borderRadius,
                cursor: 'pointer',
                background: active ? token.colorFillTertiary : undefined,
                transition: 'background 120ms ease-out',
              }}
            >
              <h2 style={{ margin: 0, flex: '1 1 auto', fontSize: fontSize.lg, lineHeight: token.lineHeightLG, fontWeight: fontWeight.title }}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={panelId}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: spacing.xs,
                    width: '100%',
                    textAlign: 'left',
                    padding: `${spacing.xxs}px 0`,
                    border: 0,
                    background: 'none',
                    cursor: 'pointer',
                    font: 'inherit',
                    color: token.colorText,
                  }}
                >
                  <DownOutlined aria-hidden style={{ fontSize: 12, transform: open ? undefined : 'rotate(-90deg)', transition: 'transform 120ms ease-out' }} />
                  {group.name}
                  <Text weight="regular" style={{ color: token.colorTextSecondary }}>
                    {formatMessage({ id: 'locations.countryGroupCount' }, { count: group.items.length })}
                  </Text>
                </button>
              </h2>
              {canCreate && group.countryCode && (
                // Opacity only, so it stays in the tab order. Wraps under the country when it does not fit beside it.
                <span className={ADD_CLASS} style={{ opacity: active ? 1 : 0, pointerEvents: active ? undefined : 'none', transition: 'opacity 120ms ease-out' }}>
                  <AddInCountryButton countryCode={group.countryCode} />
                </span>
              )}
            </div>
            <div id={panelId} hidden={!open} style={{ marginTop: spacing.sm }}>
              {renderCards(group.items, groupIndex === 0)}
            </div>
          </section>
        )
      })}
    </>
  )
}
